import { PlusIcon } from "@heroicons/react/20/solid";
import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import CreateInvoiceSidebar from "modules/billing/components/InvoicesIndexView/components/CreateInvoiceSidebar/CreateInvoiceSidebar";
import type { FunctionComponent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "~/components/buttons/Button/Button";
import Card from "~/components/Card/Card";
import SearchInput from "~/components/formElements/SearchInput/SearchInput";
import { SwitchPlain } from "~/components/formElements/Switch/Switch.tsx";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import Table from "~/components/Table/Table";
import { INVOICE_DETAILS_PATH_WITH_ORIGIN, INVOICES_PENDING_PATH } from "~/constants/appRoutes.ts";
import { INVOICES_INDEX_PAGE_STATE_KEY } from "~/constants/pageStateStorageKeys.ts";
import { useAuth } from "~/contexts/AuthContext";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import {
	applyDeliveryPeriodFilter,
} from "~/modules/billing/components/DeliveryPeriodFilter/deliveryPeriodFilterUtils.ts";
import RequiredDeliveryPeriodFilter
	from "~/modules/billing/components/DeliveryPeriodFilter/RequiredDeliveryPeriodFilter.tsx";
import {
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import DownloadInvoicesTableDataButton
	from "~/modules/billing/components/InvoicesIndexView/components/DownloadInvoicesTableDataButton/DownloadInvoicesTableDataButton.tsx";
import InvoiceClientFilter from "~/modules/billing/components/InvoicesIndexView/components/InvoiceClientFilter.tsx";
import InvoicesIndexActiveFilters
	from "~/modules/billing/components/InvoicesIndexView/components/InvoicesIndexActiveFilters.tsx";
import InvoiceStatusButtonFilter
	from "~/modules/billing/components/InvoicesIndexView/components/InvoiceStatusButtonFilter/InvoiceStatusButtonFilter.tsx";
import {
	invoicesIndexTableColumnDefs,
} from "~/modules/billing/components/InvoicesIndexView/invoicesIndexTableColumnDefs.tsx";
import type { InvoicesIndexTableData } from "~/modules/billing/components/InvoicesIndexView/InvoicesIndexTableTypes.ts";
import type { DateRange } from "~/types/dateAndTimeTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";
import type { ColumnFilterType } from "~/types/tanstackTableTypes.ts";
import {
	createColumnFilterInputHandler,
	getColumnFilterValueForKey,
	sanitiseColumnFilters,
} from "~/utils/tanStackTableUtils.ts";

import { invoicesIndexDefaultPageState } from "./invoicesIndexUtils";

type Props = {
	deliveryPeriodFilter: DateRange;
	isFetching: boolean;
	isLoading: boolean;
	setDeliveryPeriodFilter: (filter: DateRange) => void;
	setShowOverdueOnly: (showOverdueOnly: boolean) => void;
	showOverdueOnly: boolean;
	tableData: InvoicesIndexTableData[];
};

const InvoicesIndexView: FunctionComponent<Props> = ({
	deliveryPeriodFilter,
	isFetching,
	isLoading,
	setDeliveryPeriodFilter,
	setShowOverdueOnly,
	showOverdueOnly,
	tableData,
}) => {
	const { hasAnyPermission } = useAuth();
	const userCanManageInvoices = hasAnyPermission([PermissionNames.CanManageInvoices]);
	const [showCreateInvoiceSidebar, setShowCreateInvoiceSidebar] = useState<boolean>(false);
	const { pageState, setPageState } = usePageStateStorage({
		pageKey: INVOICES_INDEX_PAGE_STATE_KEY,
		defaultState: invoicesIndexDefaultPageState,
	});


	const [columnFilters, setColumnFilters] = useState<ColumnFilterType[]>(
		sanitiseColumnFilters(invoicesIndexDefaultPageState.columnFilters, pageState.columnFilters));
	const [globalFilter, setGlobalFilter] = useState<string>(pageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);

	useEffect(() => {
		setPageState(prevState => ({ ...prevState, columnFilters, globalFilter, sorting }));
	}, [columnFilters, globalFilter, setPageState, sorting]);

	const handleColumnFilterInputChange = createColumnFilterInputHandler(columnFilters, setColumnFilters);

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const navigate = useNavigate();

	const preFilteredTableData = useMemo(() => {
		if (showOverdueOnly) {
			return tableData.filter(row => row.isOverdue);
		}
		return applyDeliveryPeriodFilter(deliveryPeriodFilter, tableData);

	}, [deliveryPeriodFilter, showOverdueOnly, tableData]);

	const handleRowClick = useCallback(
		(row: Row<InvoicesIndexTableData>, event: MouseEvent) => {
			const path = INVOICE_DETAILS_PATH_WITH_ORIGIN(row.original.id, validInvoiceDetailsNavigatedFromOrigins.PROCESSED_INVOICES_INDEX);

			if (event.ctrlKey || event.metaKey) {
				window.open(path, "_blank");
			} else {
				navigate(path);
			}
		},
		[navigate],
	);

	const appliedColumnFilters = useMemo(() => {
		if (showOverdueOnly) {
			return [];
		}
		return columnFilters;
	}, [columnFilters, showOverdueOnly]);

	const table = useReactTable({
		data: preFilteredTableData,
		columns: invoicesIndexTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			columnFilters: appliedColumnFilters,
			globalFilter,
			sorting,
		},
		meta: {
			onRowClick: handleRowClick,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});

	const { rows } = table.getRowModel();

	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollContainerRef.current,
		estimateSize: () => 48.5, // Adjust based on your row height (px-4 py-3 ≈ 60px)
		overscan: 10,
	});

	return <div className={clsx("auto-grid-3-rows h-screen ")}>
		<div className="z-50 mx-auto mb-4 w-full space-y-4 bg-white px-8 py-4 ">
			<div className="flex items-center">
				<RequiredDeliveryPeriodFilter onChange={setDeliveryPeriodFilter}
											  selectedPeriod={deliveryPeriodFilter} />
				<div className="ml-auto flex items-center gap-x-2">
					{!isLoading && isFetching &&
						<div className="mr-2 flex items-center gap-x-2 text-sm font-bold text-gray-500">
							<LoadingSpinner theme="neutral" />Laden ...</div>}

					<Button theme="secondary"
							to={INVOICES_PENDING_PATH}
					><PlusIcon className="size-5" />
						Neue Projektrechnung
					</Button>

					{userCanManageInvoices && <Button theme="secondary"
													  onClick={() => setShowCreateInvoiceSidebar(true)}><PlusIcon className="size-5" />
						Neue Rechnung
					</Button>}
				</div>
			</div>
		</div>
		<div className="z-50 mb-2 w-full px-8">
			<Card shadow="sm"
				  padding="sm">
				<div className={"flex w-full items-center gap-x-4"}>
					<div className="w-64">
						<SearchInput value={globalFilter}
									 inputSize="sm"
									 autoFocus={true}
									 onChange={setGlobalFilter} />

					</div>
					<div className="w-64">
						<InvoiceClientFilter onChange={(value) => handleColumnFilterInputChange("companyShortName", value)}
											 filterValues={getColumnFilterValueForKey("companyShortName", columnFilters)} />
					</div>
					<InvoiceStatusButtonFilter
						onChange={(value) => handleColumnFilterInputChange("invoiceStatusId", value)}
						filterValues={getColumnFilterValueForKey("invoiceStatusId", columnFilters)} />


					<div className="ml-auto flex items-center gap-x-2">
						<SwitchPlain label="Alle überfälligen Rechnungen anzeigen"
									 checked={showOverdueOnly}
									 onChange={setShowOverdueOnly} />
						<DownloadInvoicesTableDataButton deliveryPeriodFilter={deliveryPeriodFilter}
														 filteredTableData={table.getFilteredRowModel().rows.map(row => row.original)}
														 search={globalFilter} />
					</div>
				</div>
			</Card>
			<InvoicesIndexActiveFilters clientFilterValue={getColumnFilterValueForKey("companyShortName", columnFilters)}
										showOverdueOnly={showOverdueOnly}
										onChangeClientFilter={(value) => handleColumnFilterInputChange("companyShortName", value)}
										onChangeStatusFilter={(value) => handleColumnFilterInputChange("invoiceStatusId", value)}
										onRemoveOverdueFilter={() => setShowOverdueOnly(false)}
										statusFilterValue={getColumnFilterValueForKey("invoiceStatusId", columnFilters)} />
		</div>
		<div className="h-full overflow-hidden px-8 pb-6">
			<Table table={table}
				   isLoading={isLoading}
				   fixedWidth={true}
				   virtualized={true}
				   scrollContainerRef={scrollContainerRef}
				   virtualizer={virtualizer} />
		</div>
		{userCanManageInvoices && <CreateInvoiceSidebar isOpen={showCreateInvoiceSidebar}
														onClose={() => setShowCreateInvoiceSidebar(false)} />}
	</div>;

};

export default InvoicesIndexView;